<template>
  <div class="upload-works-page">
    <!-- 上传作品卡片 -->
    <div class="card">
      <h3 class="card-title">上传作品 <span class="required-tip">* 为必填项</span></h3>
      <div class="video-section">
        <div class="video-player" :class="{ 'has-video': form.videoFile }">
          <!-- 未上传视频时显示上传按钮 -->
          <div v-if="!form.videoFile" class="upload-placeholder">
            <input
              ref="videoInput"
              type="file"
              accept="video/*"
              style="display: none"
              @change="handleVideoChange"
            />
            <button class="btn-upload-video" @click="selectVideo">
              <span class="upload-icon">📹</span>
              <span>上传视频</span>
            </button>
            <p class="upload-hint">支持 MP4、MOV、AVI 等常见格式</p>
          </div>
          <!-- 已上传视频时显示预览 -->
          <div v-else class="video-preview">
            <video
              ref="videoPreview"
              :src="form.videoUrl"
              controls
              class="preview-player"
            ></video>
            <div class="video-info-bar">
              <span class="video-name">{{ form.videoFile.name }}</span>
              <span class="video-size">{{ formatFileSize(form.videoFile.size) }}</span>
              <button class="btn-remove-video" @click="removeVideo">移除</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 视频资料卡片 -->
    <div class="card">
      <h3 class="card-title">视频资料</h3>
      <div class="video-info-form">
        <div class="form-row">
          <div class="form-col form-col-large">
            <label>视频介绍 <span class="required">*</span></label>
            <input
              v-model="form.intro"
              type="text"
              placeholder="请输入视频介绍，让观众了解你的作品"
              :disabled="isSubmitting"
            />
          </div>
          <div class="form-col form-col-small">
            <label>视频分类 <span class="required">*</span></label>
            <div class="custom-select" :class="{ disabled: isSubmitting }" @click="!isSubmitting && (showDropdown = !showDropdown)">
              <span class="select-text" :class="{ placeholder: !form.category }">{{ form.category || '请选择' }}</span>
              <i class="el-icon-arrow-down"></i>
              <div v-if="showDropdown" class="dropdown-menu">
                <div
                  v-for="item in categoryList"
                  :key="item.value"
                  class="dropdown-item"
                  @click.stop="selectCategory(item.label)"
                >
                  {{ item.label }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 视频封面 -->
      <div class="cover-section">
        <h4 class="cover-title">
          视频封面 <span class="required">*</span>
          <span class="cover-tip">请根据视频比例选择图片上传</span>
        </h4>
        <div class="cover-upload-area">
          <!-- 竖版封面 -->
          <div class="cover-frame cover-portrait" :class="{ 'has-cover': form.coverPortrait }">
            <input
              ref="portraitInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handlePortraitChange"
            />
            <div v-if="!form.coverPortrait" class="cover-placeholder">
              <button class="btn-cover-upload" @click="selectPortrait">上传图片</button>
              <span class="cover-ratio">竖版 9:16</span>
            </div>
            <div v-else class="cover-preview">
              <img :src="form.coverPortraitUrl" alt="竖版封面" />
              <div class="cover-actions">
                <button class="btn-edit-cover" @click="selectPortrait">更换</button>
                <button class="btn-remove-cover" @click="removePortrait">移除</button>
              </div>
            </div>
          </div>
          <!-- 横版封面 -->
          <div class="cover-frame cover-landscape" :class="{ 'has-cover': form.coverLandscape }">
            <input
              ref="landscapeInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleLandscapeChange"
            />
            <div v-if="!form.coverLandscape" class="cover-placeholder">
              <button class="btn-cover-upload" @click="selectLandscape">上传图片</button>
              <span class="cover-ratio">横版 16:9</span>
            </div>
            <div v-else class="cover-preview">
              <img :src="form.coverLandscapeUrl" alt="横版封面" />
              <div class="cover-actions">
                <button class="btn-edit-cover" @click="selectLandscape">更换</button>
                <button class="btn-remove-cover" @click="removeLandscape">移除</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 谁可以看 -->
      <div class="visibility-section">
        <span class="visibility-label">谁可以看</span>
        <label class="radio-item">
          <input v-model="form.visibility" type="radio" value="public" :disabled="isSubmitting" />
          <span class="radio-custom"></span>
          <span class="radio-text">所有人</span>
        </label>
        <label class="radio-item">
          <input v-model="form.visibility" type="radio" value="private" :disabled="isSubmitting" />
          <span class="radio-custom"></span>
          <span class="radio-text">私密</span>
        </label>
      </div>
    </div>

    <!-- 上传发布按钮 -->
    <div class="submit-section">
      <button
        class="btn-submit"
        :class="{ submitting: isSubmitting, submitted: isSubmitted }"
        :disabled="isSubmitting || isSubmitted"
        @click="submit"
      >
        <span v-if="isSubmitting" class="btn-loading">
          <span class="loading-spinner"></span>
          上传中...
        </span>
        <span v-else-if="isSubmitted">已提交审核</span>
        <span v-else>上传发布</span>
      </button>
    </div>

    <!-- 上传进度 -->
    <div v-if="isSubmitting || isSubmitted" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <span class="progress-title">上传进度</span>
          <span class="progress-status" :class="{ reviewing: isSubmitted }">{{ progressStatus }}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="progress-info">
          <span>{{ progressPercent }}%</span>
          <span v-if="isSubmitted" class="review-tip">审核预计1-3个工作日</span>
        </div>
      </div>
    </div>

    <!-- 已上传视频列表 -->
    <div v-if="uploadedVideos.length > 0" class="uploaded-list-section">
      <h3 class="section-title">我的投稿</h3>
      <div class="video-list">
        <div
          v-for="(video, index) in uploadedVideos"
          :key="video.id || index"
          class="video-list-item"
        >
          <div class="video-thumb">
            <img :src="video.coverUrl || video.coverPortraitUrl || video.coverLandscapeUrl" alt="封面" />
            <span class="video-duration">{{ video.duration || '--:--' }}</span>
          </div>
          <div class="video-meta">
            <h4 class="video-title">{{ video.title || video.intro || '未命名视频' }}</h4>
            <p class="video-desc">{{ video.category }} · {{ formatTime(video.uploadTime) }}</p>
            <div class="video-status">
              <span class="status-badge" :class="video.status">{{ getStatusText(video.status) }}</span>
            </div>
          </div>
          <div class="video-actions">
            <button class="btn-action-remove" @click="removeUploadedVideo(index)">移除</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UploadWorksPage',
  data() {
    return {
      showDropdown: false,
      isSubmitting: false,
      isSubmitted: false,
      progressPercent: 0,
      progressStatus: '准备上传',
      form: {
        intro: '',
        category: '',
        visibility: 'public',
        videoFile: null,
        videoUrl: '',
        coverPortrait: null,
        coverPortraitUrl: '',
        coverLandscape: null,
        coverLandscapeUrl: ''
      },
      categoryList: [
        { label: '音乐', value: 'music' },
        { label: '舞蹈', value: 'dance' },
        { label: '乐器', value: 'instrument' },
        { label: '脱口秀', value: 'talkshow' },
        { label: '二次元', value: 'anime' },
        { label: '颜值', value: 'beauty' },
        { label: '才艺', value: 'talent' },
        { label: '生活方式', value: 'lifestyle' }
      ],
      uploadedVideos: []
    }
  },
  methods: {
    // 选择视频文件
    selectVideo() {
      this.$refs.videoInput.click()
    },
    // 处理视频文件选择
    handleVideoChange(e) {
      const file = e.target.files[0]
      if (!file) return

      // 验证文件类型
      if (!file.type.startsWith('video/')) {
        this.$message.error('请选择视频文件')
        return
      }

      // 验证文件大小（最大2GB）
      const maxSize = 2 * 1024 * 1024 * 1024
      if (file.size > maxSize) {
        this.$message.error('视频文件不能超过2GB')
        return
      }

      this.form.videoFile = file
      this.form.videoUrl = URL.createObjectURL(file)
      this.$message.success('视频已选择：' + file.name)
    },
    // 移除视频
    removeVideo() {
      this.form.videoFile = null
      this.form.videoUrl = ''
      if (this.$refs.videoInput) {
        this.$refs.videoInput.value = ''
      }
    },
    // 选择竖版封面
    selectPortrait() {
      this.$refs.portraitInput.click()
    },
    // 处理竖版封面选择
    handlePortraitChange(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }

      this.form.coverPortrait = file
      this.form.coverPortraitUrl = URL.createObjectURL(file)
    },
    // 移除竖版封面
    removePortrait() {
      this.form.coverPortrait = null
      this.form.coverPortraitUrl = ''
      if (this.$refs.portraitInput) {
        this.$refs.portraitInput.value = ''
      }
    },
    // 选择横版封面
    selectLandscape() {
      this.$refs.landscapeInput.click()
    },
    // 处理横版封面选择
    handleLandscapeChange(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }

      this.form.coverLandscape = file
      this.form.coverLandscapeUrl = URL.createObjectURL(file)
    },
    // 移除横版封面
    removeLandscape() {
      this.form.coverLandscape = null
      this.form.coverLandscapeUrl = ''
      if (this.$refs.landscapeInput) {
        this.$refs.landscapeInput.value = ''
      }
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'reviewing': '审核中',
        'approved': '已通过',
        'rejected': '未通过'
      }
      return statusMap[status] || status
    },
    selectCategory(label) {
      this.form.category = label
      this.showDropdown = false
    },
    // 提交发布
    submit() {
      // 表单验证
      if (!this.form.videoFile) {
        this.$message.error('请上传视频文件')
        return
      }
      if (!this.form.intro.trim()) {
        this.$message.error('请输入视频介绍')
        return
      }
      if (!this.form.category) {
        this.$message.error('请选择视频分类')
        return
      }
      if (!this.form.coverPortrait && !this.form.coverLandscape) {
        this.$message.error('请至少上传一张封面图片')
        return
      }

      // 开始上传流程
      this.isSubmitting = true
      this.progressPercent = 0
      this.progressStatus = '正在上传...'

      // 模拟上传进度
      this.simulateUpload()
    },
    // 模拟上传进度
    simulateUpload() {
      const interval = setInterval(() => {
        if (this.progressPercent < 90) {
          this.progressPercent += Math.floor(Math.random() * 10) + 5
          if (this.progressPercent > 90) {
            this.progressPercent = 90
          }
        } else {
          clearInterval(interval)
          // 上传完成，进入审核状态
          setTimeout(() => {
            this.progressPercent = 100
            this.progressStatus = '审核中'
            this.isSubmitting = false
            this.isSubmitted = true
            this.$message.success('视频提交成功！审核预计1-3个工作日')

            // 保存到已上传列表
            this.saveToUploadedList()
          }, 500)
        }
      }, 300)
    },
    // 保存到已上传列表
    saveToUploadedList() {
      const videoInfo = {
        id: Date.now(),
        intro: this.form.intro,
        category: this.form.category,
        visibility: this.form.visibility,
        coverPortraitUrl: this.form.coverPortraitUrl,
        coverLandscapeUrl: this.form.coverLandscapeUrl,
        coverUrl: this.form.coverPortraitUrl || this.form.coverLandscapeUrl,
        uploadTime: new Date().toISOString(),
        status: 'reviewing',
        duration: '--:--'
      }
      this.uploadedVideos.unshift(videoInfo)

      // 清空表单
      this.resetForm()
    },
    // 重置表单
    resetForm() {
      this.form = {
        intro: '',
        category: '',
        visibility: 'public',
        videoFile: null,
        videoUrl: '',
        coverPortrait: null,
        coverPortraitUrl: '',
        coverLandscape: null,
        coverLandscapeUrl: ''
      }
      // 安全地重置文件输入框
      if (this.$refs.videoInput) {
        this.$refs.videoInput.value = ''
      }
      if (this.$refs.portraitInput) {
        this.$refs.portraitInput.value = ''
      }
      if (this.$refs.landscapeInput) {
        this.$refs.landscapeInput.value = ''
      }
    },
    // 移除已上传的视频
    removeUploadedVideo(index) {
      this.$confirm('确定要移除这个视频吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.uploadedVideos.splice(index, 1)
        this.$message.success('已移除')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
// B站直播风格令牌：粉 #fb7299 / hover #ff85ad / 浅粉 #fff0f5 #fff7fa
// 文字 #18191c #61666d #9499a0 / 边框 #e3e5e7 / 分割 #f1f2f3 / 底 #f4f4f5
// 卡片 radius 8 / 输入按钮 radius 6 / 标签 radius 4 / 过渡 0.2s / 禁止 box-shadow

// 页面容器（由外层 .content 滚动，禁止 100vh 撑高）
.upload-works-page {
  min-height: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: #f4f4f5;
  padding: 20px;
  box-sizing: border-box;
}

// 白色卡片
.card {
  background: #fff;
  border: 1px solid #e3e5e7;
  border-radius: 8px;
  padding: 22px 24px;
  margin-bottom: 16px;
}

.card-title {
  font-size: 17px;
  font-weight: 600;
  color: #18191c;
  margin: 0 0 18px;
}

// ==================== 视频上传区 ====================
.video-section {
  .video-player {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #1a1a1a;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
    overflow: hidden;

    &.has-video {
      background: #000;
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      // 主操作：上传视频
      .btn-upload-video {
        padding: 11px 34px;
        background: #fb7299;
        border: none;
        border-radius: 6px;
        color: #fff;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: background 0.2s ease;

        &:hover {
          background: #ff85ad;
        }

        .upload-icon {
          font-size: 17px;
          line-height: 1;
        }
      }

      .upload-hint {
        font-size: 12px;
        color: #9499a0;
      }
    }

    .video-preview {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;

      .preview-player {
        flex: 1;
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        margin: 0 auto;
      }

      .video-info-bar {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 10px 16px;
        background: rgba(0, 0, 0, 0.8);
        color: #fff;
        font-size: 13px;

        .video-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .video-size {
          color: #c0c4cc;
        }

        .btn-remove-video {
          padding: 4px 12px;
          background: transparent;
          border: 1px solid #f3526f;
          border-radius: 4px;
          color: #f3526f;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #f3526f;
            color: #fff;
          }
        }
      }
    }
  }
}

// ==================== 视频资料表单 ====================
.video-info-form {
  margin-bottom: 24px;

  .form-row {
    display: flex;
    gap: 16px;
  }

  .form-col {
    &.form-col-large {
      flex: 3;
    }

    &.form-col-small {
      flex: 1;
    }

    label {
      display: block;
      font-size: 13px;
      color: #61666d;
      margin-bottom: 8px;
    }

    input {
      width: 100%;
      padding: 9px 12px;
      border: 1px solid #e3e5e7;
      border-radius: 6px;
      font-size: 13px;
      color: #18191c;
      background: #fff;
      outline: none;
      box-sizing: border-box;
      transition: border-color 0.2s ease;

      &:focus {
        border-color: #fb7299;
      }

      &::placeholder {
        color: #9499a0;
      }

      &:disabled {
        background: #f7f7f8;
        color: #9499a0;
        cursor: not-allowed;
      }
    }

    // 自定义下拉（不用阴影，靠描边区分层级）
    .custom-select {
      position: relative;
      width: 100%;
      padding: 9px 12px;
      border: 1px solid #e3e5e7;
      border-radius: 6px;
      font-size: 13px;
      color: #18191c;
      background: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: #fb7299;
      }

      .select-text {
        color: #18191c;

        &.placeholder {
          color: #9499a0;
        }
      }

      .el-icon-arrow-down {
        color: #9499a0;
        font-size: 12px;
      }

      .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: #fff;
        border: 1px solid #e3e5e7;
        border-radius: 6px;
        z-index: 100;
        max-height: 200px;
        overflow-y: auto;

        .dropdown-item {
          padding: 9px 14px;
          font-size: 13px;
          color: #18191c;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #f7f7f8;
            color: #fb7299;
          }
        }
      }
    }
  }
}

// 表单禁用状态
.custom-select.disabled {
  background: #f7f7f8;
  opacity: 0.7;
  cursor: not-allowed;
}

// ==================== 封面上传区 ====================
.cover-section {
  margin-bottom: 24px;

  .cover-title {
    font-size: 15px;
    font-weight: 600;
    color: #18191c;
    margin: 0 0 16px;

    .cover-tip {
      font-size: 12px;
      color: #9499a0;
      font-weight: normal;
      margin-left: 8px;
    }
  }

  .cover-upload-area {
    display: flex;
    gap: 32px;
    justify-content: center;
    flex-wrap: wrap;

    // 虚边框拖拽区
    .cover-frame {
      border: 1px dashed #c9ccd0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      transition: border-color 0.2s ease, background 0.2s ease;
      overflow: hidden;
      position: relative;

      &:hover {
        border-color: #fb7299;
        background: #fff7fa;
      }

      &.has-cover {
        border-style: solid;
        border-color: #e3e5e7;
        background: #fff;
      }

      &.cover-portrait {
        width: 176px;
        height: 272px;
      }

      &.cover-landscape {
        width: 312px;
        height: 176px;
      }

      .cover-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;

        .btn-cover-upload {
          padding: 8px 26px;
          background: #fb7299;
          border: none;
          border-radius: 6px;
          color: #fff;
          font-size: 13px;
          cursor: pointer;
          transition: background 0.2s ease;

          &:hover {
            background: #ff85ad;
          }
        }

        .cover-ratio {
          font-size: 11px;
          color: #9499a0;
        }
      }

      .cover-preview {
        width: 100%;
        height: 100%;
        position: relative;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .cover-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          gap: 8px;
          padding: 8px;
          background: rgba(0, 0, 0, 0.6);

          button {
            flex: 1;
            padding: 6px 0;
            border: none;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
          }

          .btn-edit-cover {
            background: #fb7299;
            color: #fff;
            transition: background 0.2s ease;

            &:hover {
              background: #ff85ad;
            }
          }

          .btn-remove-cover {
            background: #f3526f;
            color: #fff;
          }
        }
      }
    }
  }
}

// ==================== 谁可以看 ====================
.visibility-section {
  display: flex;
  align-items: center;
  gap: 28px;

  .visibility-label {
    font-size: 14px;
    font-weight: 600;
    color: #18191c;
  }

  .radio-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input[type="radio"] {
      display: none;
    }

    // 圆形单选
    .radio-custom {
      width: 16px;
      height: 16px;
      border: 1.5px solid #c9ccd0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.2s ease;

      &::after {
        content: '';
        width: 8px;
        height: 8px;
        background: #fb7299;
        border-radius: 50%;
        transform: scale(0);
        transition: transform 0.2s ease;
      }
    }

    input[type="radio"]:checked + .radio-custom {
      border-color: #fb7299;

      &::after {
        transform: scale(1);
      }
    }

    .radio-text {
      font-size: 13px;
      color: #61666d;
    }
  }
}

// 必填项标记
.required {
  color: #f3526f;
  margin-left: 2px;
}

.required-tip {
  font-size: 12px;
  color: #9499a0;
  font-weight: normal;
  margin-left: 8px;
}

// ==================== 上传发布主按钮 ====================
.submit-section {
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;

  .btn-submit {
    padding: 11px 56px;
    background: #fb7299;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    min-width: 168px;

    &:hover:not(:disabled) {
      background: #ff85ad;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.8;
    }

    &.submitting {
      background: #ff85ad;
    }

    &.submitted {
      background: #36a966;
    }

    .btn-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .loading-spinner {
        width: 15px;
        height: 15px;
        border: 2px solid rgba(255, 255, 255, 0.35);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// ==================== 上传进度 ====================
.progress-section {
  margin-top: 4px;

  .progress-card {
    background: #fff;
    border: 1px solid #e3e5e7;
    border-radius: 8px;
    padding: 18px 22px;

    .progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .progress-title {
        font-size: 14px;
        font-weight: 600;
        color: #18191c;
      }

      .progress-status {
        font-size: 13px;
        color: #fb7299;
        font-weight: 500;

        &.reviewing {
          color: #ff9500;
        }
      }
    }

    .progress-bar {
      height: 6px;
      background: #f1f2f3;
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 10px;

      .progress-fill {
        height: 100%;
        background: #fb7299;
        border-radius: 4px;
        transition: width 0.3s ease;
      }
    }

    .progress-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      color: #9499a0;

      .review-tip {
        color: #ff9500;
        font-size: 12px;
      }
    }
  }
}

// ==================== 我的投稿列表 ====================
.uploaded-list-section {
  margin-top: 24px;

  .section-title {
    position: relative;
    font-size: 16px;
    font-weight: 600;
    color: #18191c;
    margin-bottom: 14px;
    padding-left: 10px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 14px;
      background: #fb7299;
      border-radius: 2px;
    }
  }

  .video-list {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .video-list-item {
      display: flex;
      align-items: center;
      gap: 16px;
      background: #fff;
      border: 1px solid #e3e5e7;
      border-radius: 8px;
      padding: 14px;

      .video-thumb {
        width: 150px;
        height: 84px;
        border-radius: 6px;
        overflow: hidden;
        position: relative;
        flex-shrink: 0;
        background: #f1f2f3;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-duration {
          position: absolute;
          bottom: 4px;
          right: 4px;
          padding: 1px 6px;
          background: rgba(0, 0, 0, 0.7);
          color: #fff;
          font-size: 11px;
          border-radius: 4px;
        }
      }

      .video-meta {
        flex: 1;
        min-width: 0;

        .video-title {
          font-size: 14px;
          font-weight: 600;
          color: #18191c;
          margin: 0 0 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .video-desc {
          font-size: 12px;
          color: #9499a0;
          margin: 0 0 8px;
        }

        .video-status {
          .status-badge {
            display: inline-block;
            padding: 2px 9px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;

            &.reviewing {
              background: #fff3e5;
              color: #ff9500;
            }

            &.approved {
              background: #e6f9ef;
              color: #23ad68;
            }

            &.rejected {
              background: #ffeef0;
              color: #f3526f;
            }
          }
        }
      }

      .video-actions {
        flex-shrink: 0;

        .btn-action-remove {
          padding: 7px 18px;
          background: transparent;
          border: 1px solid #f3526f;
          border-radius: 6px;
          color: #f3526f;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #f3526f;
            color: #fff;
          }
        }
      }
    }
  }
}
</style>